/** Sub-components for AdminConsole. Pure-presentational where possible; pass
 *  state + callbacks via props. */
import { Link } from "react-router-dom";

export const ACTION_LABELS = {
  "auth.login": "Login",
  "site.update": "Manuscript edit",
  "layers.update": "Strata edit",
  "folder.update": "Folder seal broken",
  "entity.create": "Entity inscribed",
  "entity.update": "Entity edited",
  "entity.delete": "Entity erased",
  "reality.create": "Reality inscribed",
  "reality.update": "Reality edited",
  "reality.delete": "Reality severed",
  "antecessor.update": "Antecessor inscribed",
  "admin.add": "Archivist inscribed",
  "admin.remove": "Archivist struck",
  "settings.update": "Seals reforged",
  "intruder_force.enabled": "Trapdoor armed",
  "intruder_force.disabled": "Trapdoor sealed",
  "intruder_force.entry": "Intruder slipped in",
  "intruder_force.denied": "Trapdoor rebuffed",
};

export function masterStatusText(unlocked, isMasterKey) {
  if (!unlocked) return "SEALED";
  if (isMasterKey) return "MASTER KEY — AUTO";
  return "UNSEALED";
}

export function formatTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toISOString().slice(0, 10);
    const time = d.toISOString().slice(11, 19);
    return `${date} · ${time}`;
  } catch {
    return iso;
  }
}

export function ConsoleHeader({ user, isMasterKey }) {
  return (
    <header className="space-y-4">
      <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
        Archivist Console · Limbo Protocol
      </div>
      <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tighter">
        The Inner Vault
      </h1>
      <p className="text-sm text-[#A3A3A3] max-w-2xl leading-relaxed">
        Logged as <span className="text-[#E3E1D9]">{user?.username}</span> · alias
        <span className="text-[#E3E1D9]"> {user?.alias}</span>
        {isMasterKey && (
          <> · <span className="tiv-stamp" style={{ transform: "none", marginLeft: 6 }}>MASTER KEY</span></>
        )}
      </p>
      <div className="flex flex-wrap gap-3 pt-2">
        <Link to="/admin/gridd" className="tiv-btn" data-testid="console-gridd-link">
          ◈ Neural Network · GR1DD
        </Link>
        <Link to="/chronicle" className="tiv-btn" data-testid="console-vesper-link">
          ◐ VESPER Daemon
        </Link>
      </div>
    </header>
  );
}

export function MasterSealPanel({
  isMasterKey, masterUnlocked, master, setMaster,
  tryUnlock, onReSeal,
}) {
  return (
    <section className="border border-[#333] bg-[#0a0a0a] p-6 space-y-4" data-testid="master-gate">
      <div className="section-label">
        <span>Master Seal</span>
        <span>{masterStatusText(masterUnlocked, isMasterKey)}</span>
      </div>
      {isMasterKey ? (
        <p className="text-xs text-[#A3A3A3] leading-relaxed">
          Your A-file carries the master key. The seals open at your touch — no passphrase required.
        </p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <label className="flex-1">
            <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">Master Passphrase</div>
            <input
              type="password"
              className="tiv-input"
              value={master}
              onChange={(e) => setMaster(e.target.value)}
              placeholder="speak the protocol..."
              data-testid="master-passphrase-input"
              disabled={masterUnlocked}
            />
          </label>
          {!masterUnlocked ? (
            <button className="tiv-btn" onClick={tryUnlock} data-testid="master-unlock-btn">Verify</button>
          ) : (
            <button className="tiv-btn" onClick={onReSeal} data-testid="master-lock-btn">Re-Seal</button>
          )}
        </div>
      )}
      {!masterUnlocked && !isMasterKey && (
        <p className="text-[0.65rem] tracking-[0.3em] uppercase text-[#555]">
          ::: read-only access until the seal is verified :::
        </p>
      )}
    </section>
  );
}

export function IntruderForcePanel({ settings, masterUnlocked, onToggle }) {
  const armed = settings.intruder_force_enabled;
  return (
    <section data-testid="intruder-force-section">
      <div className="section-label">
        <span>Intruder Force · Sigil Trapdoor</span>
        <span className={armed ? "text-[#A06A6A]" : "text-[#555]"}>
          {armed ? "ARMED" : "SEALED"}
        </span>
      </div>
      <div className="border border-[#333] p-5 bg-[#0a0a0a] space-y-4">
        <p className="text-[0.7rem] leading-relaxed text-[#A3A3A3]">
          Seven successive touches on the central sigil of the Threshold opens
          a side door into the archive — entering as the Intruder Force, an
          unnamed identity with edit rights but no master bypass. The door
          stays open only while this switch is armed; any administrator may
          seal it again at will. Each invocation — and every flip of this
          switch — is recorded in the Chronicle.
        </p>
        <button
          type="button"
          onClick={onToggle}
          disabled={!masterUnlocked}
          className={`flex items-center gap-3 text-xs uppercase tracking-[0.25em] transition-colors ${
            armed ? "text-[#E3E1D9]" : "text-[#555] hover:text-[#A3A3A3]"
          } ${!masterUnlocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
          data-testid="intruder-force-toggle"
          aria-pressed={armed}
        >
          <span
            className={`inline-flex w-12 h-6 border ${
              armed ? "border-[#A06A6A] bg-[#3a1a1a]" : "border-[#333] bg-[#0a0a0a]"
            } items-center transition-colors`}
          >
            <span
              className={`block w-5 h-5 mx-0.5 transition-transform ${
                armed ? "translate-x-6 bg-[#A06A6A]" : "translate-x-0 bg-[#555]"
              }`}
            />
          </span>
          {armed ? "Trapdoor armed" : "Trapdoor sealed"}
        </button>
        {!masterUnlocked && (
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555]">
            ::: master passphrase required to flip :::
          </p>
        )}
      </div>
    </section>
  );
}

export function ArchivistsPanel({
  admins, user, masterUnlocked, isMasterKey,
  newUsername, setNewUsername,
  newAlias, setNewAlias,
  newRole, setNewRole,
  onAdd, onRemove,
}) {
  return (
    <section data-testid="admins-section">
      <div className="section-label">
        <span>Archivists · The Rolls</span>
        <span>{admins.length} extant</span>
      </div>

      <div className="border-t border-l border-[#333]">
        {admins.map((a) => (
          <div key={a.id} className="grid grid-cols-12 gap-2 border-r border-b border-[#333] py-3 px-4" data-testid={`admin-row-${a.username}`}>
            <div className="col-span-3 text-sm tracking-[0.25em] text-[#E3E1D9]">{a.username}</div>
            <div className="col-span-4 font-serif-display text-xl">{a.alias}</div>
            <div className="col-span-3 text-[0.6rem] tracking-[0.3em] uppercase text-[#A3A3A3] self-center">
              {a.role === "master" ? (
                <span className="text-[#E3E1D9]">◈ master key</span>
              ) : (
                "archivist"
              )}
              {user?.username === a.username && <span className="text-[#555]"> · you</span>}
            </div>
            <div className="col-span-2 text-right">
              <button
                className="tiv-btn tiv-btn-danger text-[0.6rem]"
                onClick={() => onRemove(a.id, a.username)}
                disabled={!masterUnlocked || user?.username === a.username}
                data-testid={`admin-remove-${a.username}`}
              >
                Strike
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-[#333] mt-6 p-5 bg-[#0a0a0a] space-y-3" data-testid="add-admin-form">
        <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555]">Inscribe new archivist</div>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:items-end">
          <label className="sm:col-span-3">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">A-file number</div>
            <input className="tiv-input uppercase" placeholder="A-027" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} disabled={!masterUnlocked} data-testid="add-admin-username" />
          </label>
          <label className="sm:col-span-4">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Alias (used as password)</div>
            <input className="tiv-input" placeholder="Bastion, Nocturne..." value={newAlias} onChange={(e) => setNewAlias(e.target.value)} disabled={!masterUnlocked} data-testid="add-admin-alias" />
          </label>
          <label className="sm:col-span-2">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Role</div>
            <select className="tiv-input" value={newRole} onChange={(e) => setNewRole(e.target.value)} disabled={!masterUnlocked} data-testid="add-admin-role">
              <option value="admin" className="bg-[#0a0a0a]">archivist</option>
              <option value="master" className="bg-[#0a0a0a]" disabled={!isMasterKey}>master key</option>
            </select>
          </label>
          <div className="sm:col-span-3">
            <button className="tiv-btn w-full" onClick={onAdd} disabled={!masterUnlocked} data-testid="add-admin-submit">Inscribe</button>
          </div>
        </div>
        {!isMasterKey && (
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555]">
            ::: only a master key may inscribe another master :::
          </p>
        )}
      </div>
    </section>
  );
}

export function SealsPanel({
  settings, masterUnlocked,
  vaultPass, setVaultPass,
  masterPass, setMasterPass,
  griddPass, setGriddPass,
  onSave,
}) {
  return (
    <section data-testid="seals-section">
      <div className="section-label">
        <span>Seals · Passphrases</span>
        <span>updated {settings.updated_at?.split("T")[0]}</span>
      </div>
      <div className="border border-[#333] p-5 bg-[#0a0a0a] space-y-4">
        <label className="block">
          <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Vault Passphrase (Antecessores access)</div>
          <input className="tiv-input" value={vaultPass} onChange={(e) => setVaultPass(e.target.value)} disabled={!masterUnlocked} data-testid="seal-vault-input" />
        </label>
        <label className="block">
          <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Master Passphrase (rotates this very seal)</div>
          <input className="tiv-input" value={masterPass} onChange={(e) => setMasterPass(e.target.value)} disabled={!masterUnlocked} data-testid="seal-master-input" />
        </label>
        <label className="block">
          <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">GR1DD Passphrase (Neural Lattice access)</div>
          <input className="tiv-input" value={griddPass} onChange={(e) => setGriddPass(e.target.value)} disabled={!masterUnlocked} data-testid="seal-gridd-input" />
        </label>
        <button className="tiv-btn" onClick={onSave} disabled={!masterUnlocked} data-testid="seal-save-btn">Reforge Seals</button>
      </div>
    </section>
  );
}

export function AuditRow({ entry }) {
  return (
    <div className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-[#0f0f0f]" data-testid={`audit-row-${entry.id}`}>
      <div className="col-span-3 text-[0.65rem] tracking-[0.2em] text-[#A3A3A3] font-mono">
        {formatTimestamp(entry.timestamp)}
      </div>
      <div className="col-span-2 text-xs tracking-[0.2em]">
        <span className="text-[#E3E1D9]">{entry.actor_username}</span>
        {entry.actor_role === "master" && <span className="text-[#721C1C] ml-1">◈</span>}
        <div className="text-[0.6rem] text-[#555]">{entry.actor_alias}</div>
      </div>
      <div className="col-span-3 text-xs tracking-wide">
        <span className="text-[#E3E1D9]">{ACTION_LABELS[entry.action] || entry.action}</span>
        <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#555]">{entry.action}</div>
      </div>
      <div className="col-span-2 text-xs text-[#A3A3A3] truncate" title={entry.target}>
        {entry.target || <span className="text-[#555]">—</span>}
      </div>
      <div className="col-span-2 text-[0.7rem] text-[#A3A3A3] truncate" title={entry.details}>
        {entry.details || <span className="text-[#555]">—</span>}
      </div>
    </div>
  );
}

export function AuditLogPanel({
  audit, filteredAudit, actionOptions,
  auditFilter, setAuditFilter,
  auditActor, setAuditActor,
  onRefresh,
}) {
  return (
    <section data-testid="audit-section">
      <div className="section-label">
        <span>Audit Log · Chronicle of Hands</span>
        <span>{filteredAudit.length} / {audit.length} entries</span>
      </div>
      <div className="border border-[#333] bg-[#0a0a0a]">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 p-4 border-b border-[#333] items-end">
          <label className="sm:col-span-5">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Action</div>
            <select className="tiv-input" value={auditFilter} onChange={(e) => setAuditFilter(e.target.value)} data-testid="audit-filter-action">
              <option value="" className="bg-[#0a0a0a]">— all —</option>
              {actionOptions.map((a) => (
                <option key={a} value={a} className="bg-[#0a0a0a]">{ACTION_LABELS[a] || a}</option>
              ))}
            </select>
          </label>
          <label className="sm:col-span-4">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Actor (A-XXX)</div>
            <input
              className="tiv-input uppercase"
              placeholder="A-417, A-000..."
              value={auditActor}
              onChange={(e) => setAuditActor(e.target.value)}
              data-testid="audit-filter-actor"
            />
          </label>
          <div className="sm:col-span-3 flex justify-end">
            <button className="tiv-btn" onClick={onRefresh} data-testid="audit-refresh-btn">↻ Refresh</button>
          </div>
        </div>

        <div className="max-h-[480px] overflow-y-auto">
          {filteredAudit.length === 0 ? (
            <div className="p-10 text-center text-[#555] text-xs tracking-widest uppercase">::: no entries match :::</div>
          ) : (
            <div className="divide-y divide-[#222]">
              {filteredAudit.map((e) => <AuditRow key={e.id} entry={e} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
